import { useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Box, Button, Stack, Tooltip, Typography } from "@mui/material";
import { KeyboardBackspace } from "@mui/icons-material";
import DataGridToolbar from "../data-grid/DataGridToolBar";
import { useConfig } from "../../../contexts/ConfigContext";
import { isFulcrumMedia } from "../../../utilities/fulcrumLibService";
import { fetchItem } from "../../../utilities/dataSourceAdapters";
import { hasValidGeometry } from "../../../utilities/geoUtils";
import { entityIsMedia } from "../../../utilities/entityModalUtils";
import { mapRelationshipCategoryToTableName } from "../../../utilities/relationshipCategoryEndpoint";

import type { Entity, FlexibleDataItem, MediaItemDetailed, RelatedEntityItem } from "../../../types";
import DataListView from "../data-grid/DataListView";
import { useUISelector } from "../../../store/UI";
import RelationshipGroupView from "./RelationshipGroupView";
import { MEDIA_DRAWER_CONTENT_HEIGHT, fetchMediaUrlAndThumbnail, updateMediaList } from "../../media/MediaDrawer";
import { useSelectedEntity } from "../../../hooks/useSelectedEntity";
import { getEntityQueryKey } from "../../../api/entity";
import { useSelectionActions, useSelectionStore } from "../../../store/selection";
import { idsEqual } from "../../../utilities/idUtils";
import { useAuthSelector } from "../../../store/auth";
import { usePopupActions } from "../../../store/popupStore";
import { useMediaActions } from "../../../store/media";
import { useSpinner } from "../../../contexts/SpinnerContext";
import ProgressLoader from "./ProgressLoader";

const relationshipTableColumns = [
  {
    name: "Source",
    accessorKey: "categoryName"
  },
  {
    name: "Description",
    accessorKey: "entity_description"
  }
];

const RELATIONSHIP_DRAWER_OFFSET = 10; // 10px gap to keep the relationship drawer visually separate from the media drawer

export default function RelationshipView() {
  // entity.relationships is populated after L3 fetch (triggered by "Find Related" button)
  const { entity: selectedEntity, endpoint: selectedEndpoint, loadingProgress, isFetchingRelationships } = useSelectedEntity();
  const { setSelectedPrimaryStates, setShowRelationship } = useSelectionActions();
  const queryClient = useQueryClient();
  const { user: userProfile } = useAuthSelector();
  const { openGeoPopup, openMediaViewer, openNonGeoPopup } = usePopupActions();
  const { setMediaItems, openMediaDetail } = useMediaActions();
  const { startLoading, stopLoading } = useSpinner();

  const config = useConfig();
  const { dataGridViewMode, isMediaDrawerVisible } = useUISelector();

  const relatedItemsData = useMemo(() => {
    if (!selectedEntity?.relationships) {
      return [];
    }

    let allRelatedItems: RelatedEntityItem[] = [];

    if (selectedEntity.relationships.byTableSlug) {
      Object.entries(selectedEntity.relationships.byTableSlug).forEach(([tableSlug, items]) => {
        if (Array.isArray(items)) {
          allRelatedItems.push(...items.map((item: any) => ({ ...item, category: tableSlug })));
        }
      });
    }

    // Extract from media - respect admin setting
    if (Array.isArray(selectedEntity.relationships.media) && config.showMediaInRelatedItemsCloud) {
      const mediaWithCategory = selectedEntity.relationships.media.map((item: any) => ({
        ...item,
        category: "media",
        relationshipType: "media"
      }));
      allRelatedItems.push(...mediaWithCategory);
    }

    // Set category name aka apiEndpointName to matching category
    allRelatedItems = allRelatedItems.map((item) => {
      const apiEndPoint = config?.apiEndpoints?.find(
        (endpoint) => endpoint.tableName === item.category || endpoint.tableSlug === item.category
      );

      if (apiEndPoint && apiEndPoint.name) {
        return { ...item, categoryName: apiEndPoint.name };
      }

      // Fallback to backend-provided table_name for dynamic tables
      if ((item as any).table_name) {
        return { ...item, categoryName: (item as any).table_name };
      }

      return { ...item, categoryName: "Unknown" };
    });

    return allRelatedItems;
  }, [selectedEntity?.relationships, config.showMediaInRelatedItemsCloud, config?.apiEndpoints]);

  const handleSelectEntity = useCallback(
    async (entity: FlexibleDataItem) => {
      const category = (entity as RelatedEntityItem & { category?: string }).category;
      const fromPropsEndpoint = (entity as Entity).properties?.correctEndpoint;
      const mappedTable = category ? mapRelationshipCategoryToTableName(category, config.apiEndpoints) : null;
      const targetEndpoint =
        (mappedTable ? config.apiEndpoints.find((e) => e.tableName === mappedTable) : undefined) ?? fromPropsEndpoint ?? selectedEndpoint;

      const isFulcrumPhoto = isFulcrumMedia(entity, targetEndpoint ?? undefined);
      if (isFulcrumPhoto) {
        // Fulcrum media has no media-API record, so an L2 fetch would 404.
        // Open it in the Layer B media overlay (the same carousel the bottom
        // media drawer uses) directly from the already-available related media.
        try {
          startLoading();
          const previewItem = await fetchMediaUrlAndThumbnail(entity as MediaItemDetailed, userProfile);
          const relatedMedia = Array.isArray(selectedEntity?.relationships?.media)
            ? (selectedEntity.relationships.media as MediaItemDetailed[])
            : [];
          const mediaList = relatedMedia.length ? updateMediaList(relatedMedia, previewItem) : [previewItem];
          setMediaItems(mediaList);
          openMediaDetail(previewItem);
        } catch (error) {
          console.error("[RelationshipView] Failed to open Fulcrum media preview:", error);
        } finally {
          stopLoading();
        }
        return;
      }

      if (!targetEndpoint) {
        console.error("[RelationshipView] No endpoint for related item:", { id: entity.id, category });
        return;
      }

      setSelectedPrimaryStates({
        selectedEndpoint: targetEndpoint,
        selectedEntityId: entity.id,
        showRelationShip: false
      });

      try {
        const fullEntity = await queryClient.fetchQuery({
          queryKey: getEntityQueryKey(entity.id, targetEndpoint, "L2"),
          queryFn: () => fetchItem(entity.id, targetEndpoint, userProfile)
        });

        const { primary } = useSelectionStore.getState();
        if (!idsEqual(primary.selectedEntityId, entity.id) || primary.selectedEndpoint?.tableName !== targetEndpoint.tableName) {
          return;
        }

        if (hasValidGeometry(fullEntity)) {
          openGeoPopup();
        } else if (entityIsMedia(fullEntity)) {
          openMediaViewer();
        } else {
          openNonGeoPopup();
        }
      } catch (error) {
        console.error("[RelationshipView] Failed to load related entity for map popup:", error);
      }
    },
    [
      config.apiEndpoints,
      openGeoPopup,
      openMediaViewer,
      openNonGeoPopup,
      queryClient,
      selectedEndpoint,
      selectedEntity,
      setSelectedPrimaryStates,
      setMediaItems,
      openMediaDetail,
      startLoading,
      stopLoading,
      userProfile
    ]
  );

  return (
    <Stack gap={16} height={isMediaDrawerVisible ? `calc(100% - ${MEDIA_DRAWER_CONTENT_HEIGHT + RELATIONSHIP_DRAWER_OFFSET}px)` : "100%"}>
      <Box px={24} py={"1rem"} display='flex' sx={{ flexDirection: "column" }} justifyContent='space-between'>
        <Tooltip title='Back to grid'>
          <Button
            sx={{
              gap: 12,
              textTransform: "none",
              border: "1px solid",
              background: "primary.main",
              borderColor: "primary.main",
              borderRadius: "0.75rem"
            }}
            variant='contained'
            onClick={() => setShowRelationship(false)}
            aria-label='Back to grid'
            disableElevation
          >
            <KeyboardBackspace aria-hidden /> Back to grid
          </Button>
        </Tooltip>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 }}>
          <Typography color='grey.400' variant='body1' sx={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            Related Items ({relatedItemsData?.length || 0})
          </Typography>
          <Box sx={{ flex: "0 0 20%" }}>
            <DataGridToolbar title='' showPagination={false} showBulkEdit={false} />
          </Box>
        </Box>
      </Box>

      {/* Loading state: L3 fetch in flight */}
      {isFetchingRelationships && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
          <ProgressLoader progress={loadingProgress} />
        </Box>
      )}

      {/* Empty state: L3 done but no items found (fallback — normally auto-collapsed) */}
      {!isFetchingRelationships && relatedItemsData.length === 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", flex: 1, gap: 8 }}>
          <Typography color='grey.400' variant='body1'>
            No related items found.
          </Typography>
          <Typography color='grey.600' variant='body2'>
            This entity has no connections to other records.
          </Typography>
        </Box>
      )}

      {/* Data: render list or card grid */}
      {!isFetchingRelationships &&
        relatedItemsData.length > 0 &&
        (dataGridViewMode === "list" ? (
          <DataListView
            data={relatedItemsData as any}
            currentEndpoint={selectedEndpoint}
            onSelect={handleSelectEntity}
            columns={relationshipTableColumns}
            sx={{ height: "calc(100dvh - 131px)" }}
          />
        ) : (
          <Box component={"div"} sx={{ overflow: "auto" }} className='scroller'>
            <RelationshipGroupView compact relatedItemsData={relatedItemsData} onSelect={handleSelectEntity} />
          </Box>
        ))}
    </Stack>
  );
}
